/**
 * The end of the training course (ROUND7 §5): how it ended, then the four rules that matter most once there is
 * a ranger's life on it - the ones a new player must never learn by surprise (M7 report, "after the first human
 * playtest"). Then back to the title, or round the course again. Nothing is scored and nothing is saved here:
 * the "finished once" mark is the app's to write (src/app/training/landing.ts), so a `?ui=` fixture of this
 * screen touches no storage.
 */

import './training.css';
import { trainingDeployment } from '../../meta/training';
import type { AftermathInput, Ctx, Screen } from './context';
import { keyName } from './controls/keyNames';
import { box, button, el } from './dom';
import { frame } from './widgets/frame';
import { navHints } from './widgets/keyHints';
import { stamp, type StampSpec } from './widgets/stamp';

const COMPLETE: StampSpec = { text: 'COURSE COMPLETE', tone: 'green' };

/** The stamp and one line on how it went. Pure. */
export function trainingVerdict(input: Pick<AftermathInput, 'result' | 'note'>): StampSpec & { line: string } {
  if (input.note !== undefined && input.note !== '') {
    return { text: 'COURSE STOPPED', tone: 'amber', line: input.note };
  }
  switch (input.result.outcome) {
    case 'extracted':
      return input.result.objectiveComplete
        ? { ...COMPLETE, line: 'OBJECTIVE DONE, AND ABOARD. THAT IS HOW A MISSION ENDS WELL.' }
        : { ...COMPLETE, line: 'ABOARD - BUT THE WALL STOOD. OUT THERE, THAT IS A FAILED MISSION.' };
    case 'kia':
      return { text: 'KILLED IN TRAINING', tone: 'red', line: 'ON A MISSION, HE WOULD NOT BE COMING BACK.' };
    case 'captured':
      return { text: 'TAKEN PRISONER', tone: 'red', line: 'DOWN, WITH THE ENEMY STANDING OVER YOU: CAPTURED.' };
    case 'walkedOut':
      return { text: 'WALKED OUT', tone: 'amber', line: 'OFF THE SOUTH EDGE ON FOOT: MISSING IN ACTION.' };
    case 'scrubbed':
      return { text: 'SCRUBBED', tone: 'amber', line: 'NO JUMP, OR A LANDING IN THE BADLANDS: NO MISSION AT ALL.' };
  }
}

/** The rules the card ends on. `recallKey` is the player's own binding, printed as the HUD prints it. */
export function trainingRules(recallKey: string): string[] {
  return [
    'A RANGER WHO DIES IS GONE FOR GOOD. HIS NAME GOES ON THE MEMORIAL WALL.',
    'DOWNED USUALLY MEANS CAPTURED. A POW CAMP RAID CAN BRING PRISONERS HOME.',
    `RECALL IS THE ONLY WAY OUT OF A MISSION: HOLD ${recallKey}, THEN REACH THE LADDER.`,
    'QUITTING SUSPENDS A MISSION - IT NEVER REWINDS. WHAT HAPPENED, HAPPENED.',
  ];
}

function recallKey(ctx: Ctx): string {
  try {
    const code = ctx.bindings?.actions().find((a) => a.id === 'recall')?.keys[0];
    return `[${keyName(code ?? 'KeyG')}]`;
  } catch {
    return '[G]';
  }
}

export function trainingEndScreen(ctx: Ctx, input: AftermathInput): Screen {
  const f = frame('training', 'FIELD TRAINING', navHints('TITLE'));
  f.info.append(el('span', '', 'NOTHING AT STAKE - NOTHING RECORDED'));
  const verdict = trainingVerdict(input);

  const card = el('div', 'menu-training-card menu-col menu-grow menu-scroll');
  card.append(
    box('menu-training-stampbox', stamp(verdict)),
    el('div', 'menu-training-line menu-prose', verdict.line),
    el('div', 'menu-training-head', 'THE RULES THAT MATTER'),
  );
  const rules = el('ol', 'menu-training-rules menu-prose');
  for (const rule of trainingRules(recallKey(ctx))) rules.append(el('li', 'menu-training-rule', rule));
  card.append(rules);

  const title = button('BACK TO THE TITLE', () => ctx.nav.title(), 'menu-training-go');
  const again = button('RUN THE COURSE AGAIN', () => ctx.fly(trainingDeployment()));
  f.body.append(box('menu-col menu-grow', card, box('menu-training-actions', title, again)));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => title };
}
